import { getBingoCategory, type BingoPublicCell, type BingoCategoryId } from "@hitster/shared";

interface Props {
  cells: BingoPublicCell[];
  size: number;
  selectableIndexes?: number[];
  highlightLine?: number[];
  onSelect?: (index: number) => void;
  compact?: boolean;
  steal?: boolean;
}

export function BingoGrid({
  cells,
  size,
  selectableIndexes = [],
  highlightLine,
  onSelect,
  compact = false,
  steal = false,
}: Props) {
  const selectable = new Set(selectableIndexes);
  const highlighted = new Set(highlightLine ?? []);
  const selecting = selectable.size > 0;
  const hideNames = compact || size >= 6;

  return (
    <div
      className={`grid w-full mx-auto ${
        compact ? "max-w-[min(100%,16rem)] gap-1" : "max-w-[min(100%,28rem)] gap-1.5 sm:gap-2"
      }`}
      style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))` }}
    >
      {cells.map((cell) => {
        const cat = safeCategory(cell.categoryId);
        const canClick = selecting && selectable.has(cell.index) && onSelect;
        const dimmed = selecting && !selectable.has(cell.index);
        return (
          <button
            key={cell.index}
            type="button"
            disabled={!canClick}
            onClick={() => canClick && onSelect?.(cell.index)}
            className={`bingo-cell relative aspect-square rounded-xl border flex flex-col items-center justify-center text-center px-0.5 ${
              compact ? "text-[0.65rem]" : "text-xs sm:text-sm"
            } ${cell.checked && !steal ? "bingo-cell-checked" : ""} ${
              canClick ? (steal ? "bingo-cell-steal cursor-pointer" : "bingo-cell-pulse cursor-pointer") : ""
            } ${dimmed ? "opacity-35" : ""} ${highlighted.has(cell.index) ? "bingo-cell-win" : ""}`}
            style={{
              borderColor: canClick && steal ? "#f97316" : `${cat.color}66`,
              background: canClick && steal ? `${cat.color}28` : cell.checked ? `${cat.color}33` : `${cat.color}14`,
            }}
            aria-label={`${cat.name}${cell.checked ? " cochée" : ""}${canClick ? (steal ? ", retirer cette case" : ", disponible") : ""}`}
          >
            {canClick && steal && (
              <span
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-orange-500 text-white text-xs font-black leading-5"
                aria-hidden
              >
                ✕
              </span>
            )}
            <span className={compact ? "text-base" : "text-lg sm:text-xl"} aria-hidden>
              {cell.checked ? "✓" : cat.icon}
            </span>
            {!hideNames && (
              <span className="leading-tight font-semibold" style={{ color: cat.color }}>
                {cat.name}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function safeCategory(id: BingoCategoryId) {
  try {
    return getBingoCategory(id);
  } catch {
    return { name: id, icon: "•", color: "#8b5cf6" };
  }
}
